import { canEditLayout } from "../../utils/canEditLayout";
import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, MapPin, Share2, Check, ExternalLink, Camera, Loader2, Images, Maximize2, Filter } from "lucide-react";
import BlockShell from "./BlockShell";
import Lightbox from "../Lightbox";
import { renderInline } from "./StoryChapterBlock";
import { Button } from "../ui/button";
import { useAuth, API } from "../../App";
import { absUrl, shareLink } from "./media";
import { MapSpikingOverlay } from "../SpikingSignsBadge";

export { absUrl, shareLink };

const fmt = (d) => { if (!d) return "Date TBA"; try { return new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }); } catch { return d; } };
const isPast = (s) => Boolean(s.date) && new Date(`${s.date}T23:59:00`) < new Date();

// Teardrop pin with the flyer inside; past stops go greyscale.
const _icons = {};
const pinIcon = (s, active) => {
  const key = `${s.flyer || ""}|${isPast(s)}|${active}`;
  if (_icons[key]) return _icons[key];
  const size = active ? 58 : 46, past = isPast(s);
  const img = s.flyer ? `<img src="${absUrl(s.flyer)}" style="width:100%;height:100%;object-fit:cover;transform:rotate(45deg) scale(1.45);${past ? "filter:grayscale(1)" : ""}"/>` : `<span style="transform:rotate(45deg);font-size:${size / 2.4}px">★</span>`;
  _icons[key] = L.divIcon({
    className: "tour-stop-pin",
    html: `<div style="width:${size}px;height:${size}px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);overflow:hidden;background:${past ? "#9ca3af" : "#B8860B"};border:3px solid ${active ? "#F59E0B" : "white"};box-shadow:0 4px 10px rgba(0,0,0,.45);opacity:${past ? 0.75 : 1};display:flex;align-items:center;justify-content:center;color:#fff">${img}</div>`,
    iconSize: [size, size], iconAnchor: [size / 2, size], popupAnchor: [0, -size],
  });
  return _icons[key];
};

const FlyTo = ({ target }) => { const map = useMap(); useEffect(() => { if (target) map.flyTo([target.lat, target.lng], 14, { duration: 0.9 }); }, [target, map]); return null; };
const FitAll = ({ points }) => {
  const map = useMap(); const done = useRef(false);
  useEffect(() => { if (done.current || !points.length) return; done.current = true; points.length === 1 ? map.setView([points[0].lat, points[0].lng], 13) : map.fitBounds(L.latLngBounds(points.map((p) => [p.lat, p.lng])), { padding: [50, 50] }); }, [points, map]);
  return null;
};

const StopCard = ({ s, idx, active, onPick, onShare, onZoom, canEdit, onAddPhotos, uploading }) => {
  const past = isPast(s);
  const anchor = `stop-${idx}`;
  const zoomFlyer = () => { onPick(idx); onZoom(absUrl(s.flyer)); };
  return (
    <div id={anchor} className={`scroll-mt-24 rounded-2xl border bg-white overflow-hidden transition-shadow ${active ? "ring-2 ring-amber-400 shadow-lg" : "border-[#E2E0DC] hover:shadow-md"}`} data-testid={`tour-stop-${idx}`}>
      {s.flyer ? (
        <button type="button" onClick={zoomFlyer} className="relative block w-full group cursor-zoom-in" aria-label={`Enlarge flyer for ${s.venue || "this stop"}`} data-testid={`tour-stop-flyer-${idx}`}>
          <img src={absUrl(s.flyer)} alt={s.venue} className={`w-full aspect-[4/5] object-cover ${past ? "grayscale-[.6]" : ""}`} loading="lazy" />
          <div className={`absolute top-2 left-2 rounded-md px-2 py-1 text-center shadow ${past ? "bg-stone-700 text-white" : "bg-[#0A1626] text-[#F2CC8F]"}`}>
            <div className="text-[9px] uppercase tracking-widest leading-none">{s.date ? new Date(`${s.date}T12:00:00`).toLocaleDateString(undefined, { month: "short" }) : "TBA"}</div>
            <div className="text-lg font-extrabold leading-none">{s.date ? new Date(`${s.date}T12:00:00`).getDate() : "—"}</div>
          </div>
          {s.badge && <span className="absolute top-2 right-2 text-[10px] font-bold uppercase tracking-widest bg-amber-400 text-amber-950 px-2 py-0.5 rounded-full">{s.badge}</span>}
          <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/60 text-white text-[10px] font-semibold px-2 py-1 opacity-90 group-hover:opacity-100 transition-opacity"><Maximize2 className="h-3 w-3" /> Enlarge</span>
        </button>
      ) : (
        <button type="button" onClick={() => onPick(idx)} className={`block w-full aspect-[4/5] flex flex-col items-center justify-center gap-1 ${past ? "bg-stone-200" : "bg-[#0A1626]"} text-[#F2CC8F]`} data-testid={`tour-stop-flyer-${idx}`}>
          <div className="text-4xl font-extrabold">{s.date ? new Date(`${s.date}T12:00:00`).getDate() : "—"}</div>
          <div className="text-xs uppercase tracking-widest">{fmt(s.date)}</div>
        </button>
      )}
      <button type="button" onClick={() => onPick(idx)} className="block w-full text-left p-3" data-testid={`tour-stop-card-${idx}`}>
        <h3 className="font-extrabold text-[#1A1A1A] leading-tight text-sm">{s.venue || "Venue TBA"}</h3>
        <div className="text-[11px] text-[#5C5C5C] mt-1 space-y-0.5">
          <div className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {fmt(s.date)}{s.time ? ` · ${s.time}` : ""}</div>
          {(s.address || s.city) && <div className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {[s.address, s.city].filter(Boolean).join(", ")}</div>}
        </div>
        {active && s.note && <p className="text-xs text-[#3a3a3a] mt-2 leading-relaxed">{s.note}</p>}
      </button>
      {(s.photos || []).length > 0 && (
        <div className="px-3 pb-2 grid grid-cols-3 gap-1" data-testid={`tour-stop-photos-${idx}`}>
          {s.photos.map((p, i) => <button type="button" key={i} onClick={() => onZoom(absUrl(p))} className="cursor-zoom-in" aria-label="Enlarge photo"><img src={absUrl(p)} alt="" className="aspect-square w-full object-cover rounded-md" loading="lazy" /></button>)}
        </div>
      )}
      <div className="px-3 pb-3 flex flex-wrap gap-1.5">
        {s.link && <a href={s.link} target={/^https?:/.test(s.link) ? "_blank" : undefined} rel="noopener noreferrer"><Button size="sm" variant="outline" className="h-7 text-[11px]"><ExternalLink className="h-3 w-3 mr-1" /> Details</Button></a>}
        <Button size="sm" onClick={() => onShare(s, idx)} className="h-7 text-[11px] bg-[#0A1626] hover:bg-[#1B3A5C] text-white" data-testid={`tour-stop-share-${idx}`}><Share2 className="h-3 w-3 mr-1" /> Share</Button>
        {canEdit && (
          <label className="inline-flex items-center h-7 px-2.5 rounded-md text-[11px] font-semibold bg-amber-100 text-amber-900 border border-amber-300 cursor-pointer hover:bg-amber-200" data-testid={`tour-stop-add-photos-${idx}`}>
            {uploading === idx ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Camera className="h-3 w-3 mr-1" />} {past ? "Add photos" : "Add flyer/photos"}
            <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => onAddPhotos(idx, e.target.files)} />
          </label>
        )}
      </div>
    </div>
  );
};

const FILTERS = { upcoming: { label: "Upcoming", cls: "bg-[#B8860B]" }, past: { label: "Past", cls: "bg-stone-500" } };

const TourStopsBlock = ({ block, festival, layoutKey: layoutKeyProp }) => {
  const { user } = useAuth();
  const cfg = block.config || {};
  const layoutKey = layoutKeyProp || (festival?.slug ? `festival:${festival.slug}` : null);
  const canEdit = canEditLayout(user, layoutKey);
  const [stops, setStops] = useState(cfg.stops || []);
  useEffect(() => { setStops(cfg.stops || []); }, [cfg.stops]);
  const [activeIdx, setActiveIdx] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [zoom, setZoom] = useState(null);
  const [filter, setFilter] = useState(null);
  const markerRefs = useRef({});

  const ordered = useMemo(() => stops.map((s, i) => ({ ...s, _i: i })).sort((a, b) => (a.date || "9999").localeCompare(b.date || "9999")), [stops]);
  const upcoming = ordered.filter((s) => !isPast(s)), past = [...ordered.filter(isPast)].reverse();
  const mappable = ordered.filter((s) => s.lat && s.lng).map((s) => ({ ...s, lat: Number(s.lat), lng: Number(s.lng) }));
  const shown = filter === "upcoming" ? mappable.filter((s) => !isPast(s)) : filter === "past" ? mappable.filter(isPast) : mappable;
  const active = mappable.find((s) => s._i === activeIdx) || null;
  const pageTitle = festival?.title || block.title || "Tour";

  useEffect(() => { if (activeIdx == null) return; const t = setTimeout(() => markerRefs.current[activeIdx]?.openPopup(), 950); return () => clearTimeout(t); }, [activeIdx]);
  const pick = (i) => { setActiveIdx(i); document.getElementById(`tour-map-${block.block_id}`)?.scrollIntoView({ behavior: "smooth", block: "nearest" }); };
  const share = (s, i) => shareLink({ title: `${pageTitle} · ${s.venue || "Tour stop"}`, text: `${fmt(s.date)}${s.time ? ` · ${s.time}` : ""} — ${s.venue || ""}${s.city ? `, ${s.city}` : ""}. ${s.note || ""}`.trim(), url: `${window.location.origin}${window.location.pathname}#stop-${i}` });
  const toggleFilter = (f) => { setFilter((cur) => (cur === f ? null : f)); setActiveIdx(null); };

  const addPhotos = async (i, files) => {
    if (!files?.length || !layoutKey) return;
    setUploading(i);
    try {
      const urls = [];
      for (const f of Array.from(files)) {
        const fd = new FormData(); fd.append("file", f);
        const r = await axios.post(`${API}/admin/page-layouts/${encodeURIComponent(layoutKey)}/upload`, fd, { withCredentials: true });
        urls.push(r.data.url);
      }
      const next = stops.map((s, j) => (j !== i ? s : { ...s, flyer: s.flyer || (isPast(s) ? s.flyer : urls[0]), photos: [...(s.photos || []), ...urls] }));
      await axios.patch(`${API}/admin/page-layouts/${encodeURIComponent(layoutKey)}/blocks/${block.block_id}`, { config: { ...cfg, stops: next } }, { withCredentials: true });
      setStops(next);
      toast.success(`${urls.length} photo${urls.length > 1 ? "s" : ""} added — share the stop to post them`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); } finally { setUploading(null); }
  };

  const Section = ({ title, items, hint }) => (
    <div className="mb-8">
      <div className="flex items-baseline justify-between mb-3"><h3 className="text-lg font-extrabold text-[#1A1A1A]">{title} <span className="text-sm font-semibold text-[#5C5C5C]">({items.length})</span></h3>{hint && <span className="text-xs text-[#5C5C5C]">{hint}</span>}</div>
      {items.length === 0 ? <p className="text-sm text-[#5C5C5C] italic">Nothing here yet.</p> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map((s) => <StopCard key={s._i} s={s} idx={s._i} active={activeIdx === s._i} onPick={pick} onShare={share} onZoom={setZoom} canEdit={canEdit} onAddPhotos={addPhotos} uploading={uploading} />)}
        </div>
      )}
    </div>
  );

  const other = filter === "upcoming" ? "past" : "upcoming";

  return (
    <BlockShell block={block}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-sm md:text-base text-[#5C5C5C] max-w-3xl leading-relaxed">{cfg.intro ? renderInline(cfg.intro, "#B8860B") : "Tap a flyer to enlarge it and light it up on the map. Past stops turn grey — the route shows where the tour has been."}</p>
        <Button size="sm" variant="outline" onClick={() => shareLink({ title: pageTitle, text: festival?.tagline || "", url: `${window.location.origin}${window.location.pathname}` })} className="text-xs" data-testid="tour-share-page"><Share2 className="h-3.5 w-3.5 mr-1" /> Share the whole tour</Button>
      </div>
      {mappable.length > 0 && (
        <div id={`tour-map-${block.block_id}`} className="rounded-2xl overflow-hidden border-2 border-amber-300/70 shadow-lg mb-4 relative" data-testid="tour-stops-map">
          <MapSpikingOverlay testId="tour-spiking-badge" />
          <div className="absolute z-[500] top-3 right-3 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest" data-testid="tour-map-filters">
            {Object.entries(FILTERS).map(([k, f]) => (
              <button key={k} type="button" onClick={() => toggleFilter(k)} aria-pressed={filter === k} title={filter === k ? "Show all stops" : `Show only ${f.label.toLowerCase()} stops`}
                className={`${f.cls} text-white px-2.5 py-1.5 rounded shadow transition-all hover:scale-105 ${filter === k ? "ring-2 ring-white ring-offset-2 ring-offset-amber-400" : filter ? "opacity-50" : ""}`} data-testid={`tour-filter-${k}`}>
                {f.label}{filter === k ? " ✓" : ""}
              </button>
            ))}
          </div>
          <MapContainer center={[mappable[0].lat, mappable[0].lng]} zoom={11} scrollWheelZoom={false} className="h-[420px] w-full">
            <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitAll key={filter || "all"} points={shown} /><FlyTo target={active} />
            {shown.length > 1 && <Polyline positions={shown.map((s) => [s.lat, s.lng])} pathOptions={{ color: "#B8860B", weight: 3, dashArray: "8 10", opacity: 0.8 }} />}
            {shown.map((s) => (
              <Marker key={s._i} position={[s.lat, s.lng]} icon={pinIcon(s, s._i === activeIdx)} ref={(m) => { if (m) markerRefs.current[s._i] = m; }} eventHandlers={{ click: () => setActiveIdx(s._i) }}>
                <Popup maxWidth={280}>
                  <div className="min-w-[220px]" data-testid={`tour-popup-${s._i}`}>
                    {s.flyer && <button type="button" onClick={() => setZoom(absUrl(s.flyer))} className="block w-full cursor-zoom-in" aria-label="Enlarge flyer"><img src={absUrl(s.flyer)} alt="" className={`w-full h-28 object-cover rounded mb-2 ${isPast(s) ? "grayscale" : ""}`} /></button>}
                    <div className="font-extrabold text-[#1A1A1A] leading-tight">{s.venue}</div>
                    <div className="text-xs text-[#5C5C5C]">{fmt(s.date)}{s.time ? ` · ${s.time}` : ""}<br />{[s.address, s.city].filter(Boolean).join(", ")}</div>
                    {s.note && <p className="text-xs text-[#3a3a3a] mt-1">{s.note}</p>}
                    {(s.photos || []).length > 0 && <div className="text-[11px] font-bold text-amber-700 mt-1 inline-flex items-center gap-1"><Images className="h-3 w-3" /> {s.photos.length} photo{s.photos.length > 1 ? "s" : ""} from this stop ↓</div>}
                    <a href={`#stop-${s._i}`} className="block mt-2 text-xs font-bold text-[#1f4d3a] underline">Open the card ↓</a>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      )}
      {filter && (
        <div className="mb-8 rounded-2xl bg-[#0A1626] text-white p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-5 shadow-lg" data-testid="tour-filter-banner">
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 text-[#F2CC8F] font-extrabold text-base"><Filter className="h-4 w-4" /> Showing only {FILTERS[filter].label.toLowerCase()} stops ({filter === "upcoming" ? upcoming.length : past.length})</div>
            <p className="text-sm text-white/80 mt-1">The map and the list below are filtered. Use the buttons here (or the {FILTERS[filter].label} button on the map) to switch or see everything again.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => toggleFilter(other)} className={`${FILTERS[other].cls} hover:opacity-90 text-white font-bold`} data-testid="tour-filter-switch">Show {FILTERS[other].label.toLowerCase()} stops</Button>
            <Button variant="outline" onClick={() => setFilter(null)} className="bg-white text-[#0A1626] font-bold hover:bg-amber-50" data-testid="tour-filter-clear">Show everything</Button>
          </div>
        </div>
      )}
      {filter !== "past" && <Section title="Where she'll be" items={upcoming} hint="Upcoming" />}
      {filter !== "upcoming" && <Section title="Where she's been" items={past} hint="Photos and recaps live under each stop" />}
      {canEdit && <p className="text-xs text-[#5C5C5C] italic flex items-center gap-1"><Check className="h-3 w-3" /> You're signed in as an editor: use "Add photos" on any stop, or Edit this block to change dates, venues and flyers.</p>}
      <Lightbox src={zoom} onClose={() => setZoom(null)} />
    </BlockShell>
  );
};

export default TourStopsBlock;
